import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, MapPin, X } from "lucide-react";
import { Link } from "react-router-dom";

import type { Profile } from "../../api/interface";
import { profileService } from "../../api/services";
import { env } from "../../config/env";
import type { ProfileAdItem } from "./types";

interface ProfileAdPopupProps {
  profiles?: ProfileAdItem[];
  intervalMs?: number;
  visibleDurationMs?: number;
  firstAppearanceDelayMs?: number;
  profilePath?: (profile: ProfileAdItem) => string;
}

type ApiProfile = Profile & {
  title?: string | null;
  short_description?: string | null;
  price?: string | number | null;
  price_min?: string | number | null;
  is_verified?: boolean;
};

const PROFILE_PLACEHOLDER = "/logo.svg";

let profileAdsRequest: Promise<ProfileAdItem[]> | null = null;

const getImageUrl = (image?: string | null): string => {
  if (!image) {
    return PROFILE_PLACEHOLDER;
  }

  const normalizedImage = image.trim().replace(/\\/g, "/");

  if (
    normalizedImage.startsWith("http://") ||
    normalizedImage.startsWith("https://") ||
    normalizedImage.startsWith("data:") ||
    normalizedImage.startsWith("blob:")
  ) {
    return normalizedImage;
  }

  // Le backend renvoie déjà /storage/...
  if (normalizedImage.startsWith("/storage/")) {
    return `${env.IMAGE_BASE_URL_SANS_STORAGE}${normalizedImage}`;
  }

  // Le backend renvoie storage/... sans slash initial.
  if (normalizedImage.startsWith("storage/")) {
    return `${env.IMAGE_BASE_URL_SANS_STORAGE}/${normalizedImage}`;
  }

  // Le backend renvoie seulement profiles/... ou logo/...
  return `${env.IMAGE_BASE_URL}/${normalizedImage.replace(/^\/+/, "")}`;
};

const getProfileName = (profile: ApiProfile): string => {
  return (
    profile.display_name ||
    [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
    "Profil professionnel"
  );
};

const formatProfilePrice = (profile: ApiProfile): string | undefined => {
  const price = profile.price_min ?? profile.price ?? profile.price_from;

  if (price == null || !profile.currency) {
    return undefined;
  }

  const numericPrice = Number(price);

  if (Number.isNaN(numericPrice)) {
    return undefined;
  }

  try {
    const formattedPrice = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      
      currency: profile.currency,
      maximumFractionDigits: 0,
    }).format(numericPrice);

    return profile.price_unit
      ? `Dès ${formattedPrice} / ${profile.price_unit}`
      : `Dès ${formattedPrice}`;
  } catch {
    return `Dès ${price} ${profile.currency}`;
  }
};

const loadProfileAds = async (): Promise<ProfileAdItem[]> => {
  const profilesResponse = await profileService.getAll({
    page: 1,
    per_page: 10,
    status: "published",
    featured: 1,
  });

  const profiles = (profilesResponse.data ?? []) as ApiProfile[];

  return Promise.all(
    profiles.map(async (profile): Promise<ProfileAdItem> => {
      let image = PROFILE_PLACEHOLDER;

      try {
        const photosResponse = await profileService.getPhotos(profile.slug);
        const photos = photosResponse.data ?? [];

        const primaryPhoto =
          photos.find((photo) => photo.is_primary === true) ?? photos[0];

        image = getImageUrl(primaryPhoto?.path ?? primaryPhoto?.url);
      } catch (error: unknown) {
        console.error(
          `Impossible de récupérer la photo du profil ${profile.slug} :`,
          error
        );
      }

      return {
        id: profile.id,
        slug: profile.slug,
        name: getProfileName(profile),
        age: profile.age ?? undefined,
        image,
        verified: Boolean(profile.is_verified ?? profile.verified),
        location: [profile.city, profile.country].filter(Boolean).join(", "),
        category:
          profile.title ||
          profile.profession ||
          "Professionnel",
        description:
          profile.short_description ||
          profile.description ||
          "Découvrez ce profil professionnel.",
        priceLabel: formatProfilePrice(profile),
      };
    })
  );
};

const getProfileAds = (): Promise<ProfileAdItem[]> => {
  if (!profileAdsRequest) {
    profileAdsRequest = loadProfileAds().catch((error: unknown) => {
      profileAdsRequest = null;
      throw error;
    });
  }

  return profileAdsRequest;
};

const ProfileAdPopup: React.FC<ProfileAdPopupProps> = ({
  profiles: providedProfiles,
  intervalMs = 20_000,
  visibleDurationMs = 8_000,
  firstAppearanceDelayMs = 1_500,
  profilePath = (profile) => `/profils/${profile.slug}`,
}) => {
  const [apiProfiles, setApiProfiles] = useState<ProfileAdItem[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const hideTimerRef = useRef<number | null>(null);
  const transitionTimerRef = useRef<number | null>(null);

  const profiles = providedProfiles ?? apiProfiles;

  useEffect(() => {
    if (providedProfiles) {
      return undefined;
    }

    let isMounted = true;

    getProfileAds()
      .then((loadedProfiles) => {
        if (!isMounted) {
          return;
        }

        setApiProfiles(loadedProfiles);
      })
      .catch((error: unknown) => {
        console.error(
          "Impossible de charger les publicités de profils :",
          error
        );

        if (isMounted) {
          setApiProfiles([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [providedProfiles]);

  useEffect(() => {
    if (profiles.length === 0) {
      return undefined;
    }

    const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const clearTransitionTimer = () => {
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };

    const showAdvertisement = () => {
      clearHideTimer();
      setIsVisible(true);

      hideTimerRef.current = window.setTimeout(() => {
        setIsVisible(false);
        hideTimerRef.current = null;
      }, visibleDurationMs);
    };

    const firstTimer = window.setTimeout(() => {
      showAdvertisement();
    }, firstAppearanceDelayMs);

    const cycleTimer = window.setInterval(() => {
      clearHideTimer();
      clearTransitionTimer();

      setIsVisible(false);

      transitionTimerRef.current = window.setTimeout(() => {
        setCurrentIndex((current) => {
          return (current + 1) % profiles.length;
        });

        showAdvertisement();
        transitionTimerRef.current = null;
      }, 400);
    }, intervalMs);

    return () => {
      window.clearTimeout(firstTimer);
      window.clearInterval(cycleTimer);
      clearHideTimer();
      clearTransitionTimer();
    };
  }, [
    firstAppearanceDelayMs,
    intervalMs,
    profiles.length,
    visibleDurationMs,
  ]);

  useEffect(() => {
    if (profiles.length > 0 && currentIndex >= profiles.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, profiles.length]);

  if (profiles.length === 0) {
    return null;
  }

  const profile = profiles[currentIndex % profiles.length];

  const closeAdvertisement = () => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    setIsVisible(false);
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.aside
          key={profile.id}
          initial={{
            opacity: 0,
            x: 120,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            x: 0,
            scale: 1,
          }}
          exit={{
            opacity: 0,
            x: 120,
            scale: 0.95,
          }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 24,
          }}
          className="fixed bottom-3 right-3 z-[70] w-72 max-w-[78vw] overflow-hidden rounded-xl border border-white/20 bg-slate-950 text-white shadow-2xl sm:bottom-6 sm:right-6 sm:w-96 sm:max-w-sm sm:rounded-2xl"
          role="dialog"
          aria-label={`Profil mis en avant : ${profile.name}`}
        >
          <button
            type="button"
            onClick={closeAdvertisement}
            className="absolute z-20 grid w-8 h-8 text-white transition rounded-full right-2 top-2 place-items-center bg-black/70 backdrop-blur-sm hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-3 sm:top-3 sm:h-9 sm:w-9"
            aria-label="Fermer la publicité"
          >
            <X size={18} aria-hidden="true" />
          </button>

          <Link
            to={profilePath(profile)}
            className="block group"
            aria-label={`Voir le profil de ${profile.name}`}
          >
            <div className="relative h-32 overflow-hidden sm:h-52">
              <img
                src={profile.image}
                alt={`Profil de ${profile.name}`}
                onError={(event) => {
                  const image = event.currentTarget;

                  image.onerror = null;
                  image.src = PROFILE_PLACEHOLDER;
                }}
                className="object-cover w-full h-full transition duration-500 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />

              <span className="absolute left-2 top-2 rounded-full bg-primary-600 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide shadow-lg sm:left-3 sm:top-3 sm:px-3 sm:text-xs">
                Profil à découvrir
              </span>
            </div>

            <div className="relative p-3 pt-0 -mt-8 sm:-mt-12 sm:p-4 sm:pt-0">
              <div className="mb-1 flex items-center gap-1.5">
                <h3 className="text-base font-bold truncate sm:text-xl">
                  {profile.name}
                  {profile.age ? `, ${profile.age}` : ""}
                </h3>

                {profile.verified && (
                  <BadgeCheck
                    size={18}
                    className="shrink-0 text-primary-400"
                    aria-label="Profil vérifié"
                  />
                )}
              </div>

              <div className="flex items-center gap-1 mb-2 overflow-hidden text-xs text-slate-300 sm:text-sm">
                <MapPin size={14} className="shrink-0" aria-hidden="true" />

                {profile.location && (
                  <span className="truncate">
                    {profile.location}
                  </span>
                )}

                {profile.location && profile.category && (
                  <span aria-hidden="true">•</span>
                )}

                {profile.category && (
                  <span className="truncate">
                    {profile.category}
                  </span>
                )}
              </div>

              <p className="text-xs leading-4 line-clamp-2 text-slate-200 sm:text-sm sm:leading-5">
                {profile.description}
              </p>

              <div className="mt-3 flex items-center justify-between gap-2 border-t border-white/10 pt-2.5 sm:mt-4 sm:gap-3 sm:pt-3">
                <span className="text-xs font-semibold truncate text-primary-300 sm:text-sm">
                  {profile.priceLabel ?? "Voir les tarifs"}
                </span>

                <span className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-950 transition group-hover:bg-primary-400 sm:px-4 sm:py-2 sm:text-sm">
                  Voir
                  <span className="hidden sm:inline">
                    {" "}le profil
                  </span>
                </span>
              </div>
            </div>
          </Link>

          <motion.div
            key={`${profile.id}-${isVisible}`}
            initial={{
              scaleX: 0,
            }}
            animate={{
              scaleX: 1,
            }}
            transition={{
              duration: visibleDurationMs / 1000,
              ease: "linear",
            }}
            className="absolute bottom-0 left-0 w-full h-1 origin-left bg-primary-400"
          />
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default ProfileAdPopup;